import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import imageCropOptions from '../../../constants/imageCropOptions';
import ProductDetailBase from './Base';
import ProductDetail1 from './Layout1';
import ProductDetail2 from './Layout2';
import ProductDetail3 from './Layout3';
import { LAYOUT1, LAYOUT2, LAYOUT3 } from '../../../constants/pdpLayouts';
import * as Metrics from '../utils/trackMetric';
import * as MetricEvents from '../../../constants/metrics';
import useExperiment from '../../hooks/useExperiment';
import { HIVEMIND_ON, HIVEMIND_OFF } from '../../../constants/hivemind';
import useDevice, { devices } from '../../hooks/useDevice';

const layouts = {
  [LAYOUT1]: ProductDetail1,
  [LAYOUT2]: ProductDetail2,
  [LAYOUT3]: ProductDetail3
};

const layoutImpressionMap = {
  [LAYOUT1]: MetricEvents.PRODUCT_DETAIL_LAYOUT_1_IMPRESSION,
  [LAYOUT2]: MetricEvents.PRODUCT_DETAIL_LAYOUT_2_IMPRESSION,
  [LAYOUT3]: MetricEvents.PRODUCT_DETAIL_LAYOUT_3_IMPRESSION
};

export function ProductDetail({ pdpLayout, pdpImageCropMethod, olsProxyBaseUrl, ...rest }) {
  const { viewDevice, renderMode } = rest;
  const ProductDetailLayout = layouts[pdpLayout] || layouts[LAYOUT1];
  const [isExperimentEnabled, setIsExperimentEnabled] = useState(false);
  const [isStickyCheckoutFooterEnabled, setIsStickyCheckoutFooterEnabled] = useState(false);
  const [device] = useDevice();
  const isPublish = renderMode === 'PUBLISH';
  const isPublishOrPreview = renderMode === 'PUBLISH' || renderMode === 'PREVIEW';
  const isMobile = device === devices.MOBILE || viewDevice === 'MOBILE_RENDER_DEVICE';
  const { bucket } = useExperiment(
    `${olsProxyBaseUrl}/experiments/ols_low_inventory_in_product_gallery`
  );

  const { bucket: stickyFooterBucket } = useExperiment(
    `${olsProxyBaseUrl}/experiments/ols_mobile_checkout_buttons_sticky_footer?isMobile=${isMobile}`
  );

  useEffect(() => {
    /**
     * The impressions are tracked here instead of ProductLayout1 and ProductLayout2 because the
     * impressions are triggered more than once on mount there, which would produce inaccurate data.
     */
    Metrics.track(layoutImpressionMap[pdpLayout]);
  }, []);

  useEffect(() => {
    if (HIVEMIND_ON === stickyFooterBucket && isPublishOrPreview) {
      setIsStickyCheckoutFooterEnabled(true);
      if (isPublish) {
        Metrics.track(MetricEvents.OLS_MOBILE_CHECKOUT_BUTTONS_STICKY_FOOTER_ON_IMPRESSION);
      }
    } else if (HIVEMIND_OFF === stickyFooterBucket && isPublishOrPreview) {
      setIsStickyCheckoutFooterEnabled(false);
      if (isPublish) {
        Metrics.track(MetricEvents.OLS_MOBILE_CHECKOUT_BUTTONS_STICKY_FOOTER_OFF_IMPRESSION);
      }
    } else {
      setIsStickyCheckoutFooterEnabled(false);
    }
  }, [stickyFooterBucket, isPublishOrPreview, isPublish]);

  useEffect(() => {
    setIsExperimentEnabled([HIVEMIND_ON].includes(bucket));
  }, [bucket]);

  useEffect(() => {
    if (renderMode !== 'PUBLISH' && isStickyCheckoutFooterEnabled) return;
    const widget = document.getElementById('reamaze-widget');
    const moveWidgetUp = () => {
      const footer = document.getElementById('checkout-buttons-footer');

      if (widget && footer) {
        const footerHeight = footer.offsetHeight || 0;
        widget.style.setProperty('bottom', `${22 + footerHeight}px`);
      }
    };

    const interval = setInterval(() => {
      moveWidgetUp();
    }, 200);

    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      if (widget) {
        widget.style.setProperty('bottom', '22px');
      }
    };
  }, [renderMode, isStickyCheckoutFooterEnabled]);

  return (
    <ProductDetailBase
      { ...rest }
      render={ props => (
        <ProductDetailLayout
          { ...props }
          pdpLayout={ pdpLayout }
          pdpImageCropMethod={ pdpImageCropMethod }
          isLowInventoryInGalleryEnabled={ isExperimentEnabled }
          isMobile={ isMobile }
          isStickyCheckoutFooterEnabled={ isStickyCheckoutFooterEnabled }
        />
      ) }
    />
  );
}

ProductDetail.propTypes = {
  pdpLayout: PropTypes.string.isRequired,
  pdpImageCropMethod: PropTypes.string,
  olsProxyBaseUrl: PropTypes.string,
  viewDevice: PropTypes.string,
  renderMode: PropTypes.string
};

ProductDetail.defaultProps = {
  pdpLayout: LAYOUT1,
  pdpImageCropMethod: imageCropOptions.SHRINK_TO_FIT
};

export default ProductDetail;
